import {
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type {
  ActionType,
  ProColumns,
  ProDescriptionsItemProps,
} from '@ant-design/pro-components';
import {
  FooterToolbar,
  PageContainer,
  ProDescriptions,
  ProTable,
} from '@ant-design/pro-components';
import type { ProFormInstance } from '@ant-design/pro-form';
import { Button, Drawer } from 'antd';
import React, { useCallback, useRef, useState } from 'react';
import {
  convertFormFieldsToColumns,
  getDefaultCollapsedCount,
} from './TableForm';
import type {
  CommonTableProps,
  FormFieldConfig,
  SimpleSearchItem,
  TableActionConfig,
} from './types';

function CommonTable<T extends Record<string, any> = any>(
  props: CommonTableProps<T>,
) {
  const {
    title,
    columns,
    // 简洁模式
    dataFunc,
    searchFormItems = [],
    isPagination = true,
    pageSize = 10,
    // 原有模式
    request,
    manualRequest = false,
    rowKey = 'id',
    showCreateButton = false,
    createButtonText = '新建',
    onCreateClick,
    customActions = [],
    enableRowSelection = false,
    rowSelection: rowSelectionConfig,
    formFields = [],
    formDefaultCollapsed = 3,
    searchLabelWidth = 80,
    toolBarRender,
    batchActions = [],
    detailDrawer,
    actionRef: externalActionRef,
    headerExtra,
    ...restProps
  } = props;

  const internalActionRef = useRef<ActionType>(null);
  const actionRef = externalActionRef || internalActionRef;
  const formRef = useRef<ProFormInstance>(undefined);
  const [selectedRows, setSelectedRows] = useState<T[]>([]);
  const [showDetail, setShowDetail] = useState(false);
  const [currentRow, setCurrentRow] = useState<T>();

  // 处理新建按钮点击
  const handleCreateClick = () => {
    if (onCreateClick) {
      onCreateClick();
    }
  };

  // 处理自定义操作按钮点击
  const handleActionClick = (action: TableActionConfig<T>, record: T) => {
    if (action.onClick) {
      action.onClick(record);
    }
  };

  // 简洁模式：将 searchFormItems 转换为 formFields 格式
  const simpleFormFields: FormFieldConfig[] = searchFormItems.map(
    (item: SimpleSearchItem) => ({
      name: item.name,
      label: item.label,
      type: item.type || 'text',
      placeholder: item.placeholder,
      options: item.options,
      initialValue: item.initialValue,
    }),
  );

  // 使用简洁模式还是原有模式
  const isSimpleMode = !!dataFunc;
  const finalFormFields = isSimpleMode ? simpleFormFields : formFields;

  // 简洁模式：处理数据请求
  const handleDataRequest = useCallback(
    async (params: any) => {
      // 从 formRef 获取表单值并合并到 params
      const formValues = formRef.current?.getFieldsFormatValue?.() || {};
      const mergedParams = { ...formValues, ...params };
      if (!dataFunc) return { data: [], success: false, total: 0 };
      try {
        const res = await dataFunc(mergedParams);
        if (res.code === 200) {
          const data = res.data?.records || res.data?.list || res.data || [];
          return {
            data,
            success: true,
            total: res.data?.total || (Array.isArray(data) ? data.length : 0),
          };
        }
        return { data: [], success: false, total: 0 };
      } catch {
        return { data: [], success: false, total: 0 };
      }
    },
    [dataFunc],
  );

  // 构建操作列
  const buildActionColumn = (): ProColumns<T> | null => {
    if (customActions.length === 0 && !detailDrawer?.enabled) {
      return null;
    }

    return {
      title: '操作',
      dataIndex: 'option',
      valueType: 'option',
      render: (_, record) => {
        const actionItems: React.ReactNode[] = [];

        // 添加详情按钮（如果启用）
        if (detailDrawer?.enabled) {
          actionItems.push(
            <a
              key="detail"
              onClick={() => {
                setCurrentRow(record);
                setShowDetail(true);
              }}
            >
              详情
            </a>,
          );
        }

        // 添加自定义操作按钮
        customActions.forEach((action, index) => {
          const visible = action.visible ? action.visible(record) : true;
          if (visible) {
            actionItems.push(
              <a
                key={action.label || index}
                onClick={() => handleActionClick(action, record)}
                style={action.danger ? { color: '#ff4d4f' } : undefined}
              >
                {action.icon}
                {action.label}
              </a>,
            );
          }
        });

        return actionItems;
      },
    };
  };

  // 将表单字段转换为列配置
  const formColumns = convertFormFieldsToColumns(finalFormFields);

  // 处理 columns：默认不在搜索表单中显示，除非显式设置 hideInSearch: false
  const processedColumns = columns.map((col) => {
    // 如果字段已经在 formFields 中配置，或者没有显式设置 hideInSearch，则默认隐藏
    if (col.hideInSearch === undefined) {
      return {
        ...col,
        hideInSearch: true,
      };
    }
    return col;
  });

  // 构建最终列配置（表单列 + 数据列 + 操作列）
  const finalColumns: ProColumns<T>[] = [...formColumns, ...processedColumns];
  const actionColumn = buildActionColumn();
  if (actionColumn) {
    finalColumns.push(actionColumn);
  }

  // 构建工具栏
  const buildToolBar = (): React.ReactNode[] | undefined => {
    const toolBarItems: React.ReactNode[] = [];

    // 添加新建按钮
    if (showCreateButton) {
      toolBarItems.push(
        <Button type="primary" key="create" onClick={handleCreateClick}>
          <PlusOutlined /> {createButtonText}
        </Button>,
      );
    }

    // 添加自定义工具栏按钮
    if (toolBarRender) {
      const customItems = toolBarRender();
      if (customItems) {
        toolBarItems.push(...customItems);
      }
    }

    return toolBarItems.length > 0 ? toolBarItems : undefined;
  };

  // 构建行选择配置
  const buildRowSelection = () => {
    if (!enableRowSelection) {
      return undefined;
    }

    return {
      onChange: (selectedRowKeys: React.Key[], selectedRows: T[]) => {
        setSelectedRows(selectedRows);
        if (rowSelectionConfig?.onChange) {
          rowSelectionConfig.onChange(selectedRowKeys, selectedRows);
        }
      },
      onSelect: rowSelectionConfig?.onSelect,
      onSelectAll: rowSelectionConfig?.onSelectAll,
    };
  };

  // 构建搜索表单配置
  const buildSearchForm = () => {
    const defaultCollapsed = getDefaultCollapsedCount(
      finalFormFields,
      formDefaultCollapsed,
    );

    return {
      labelWidth: searchLabelWidth,
      defaultCollapsed,
      searchText: '查询',
      resetText: '重置',
      // 使用 ProTable 约定的签名拿到 form，从而在不同版本下也能稳定渲染按钮
      optionRender: (_searchConfig: any, { form }: any) => [
        <Button
          key="search"
          type="primary"
          icon={<SearchOutlined />}
          onClick={() => form?.submit?.()}
        >
          查询
        </Button>,
        <Button
          key="reset"
          icon={<ReloadOutlined />}
          onClick={() => form?.resetFields?.()}
        >
          重置
        </Button>,
      ],
    };
  };

  return (
    <PageContainer title={false}>
      {headerExtra}
      <ProTable<T>
        headerTitle={title}
        actionRef={actionRef}
        formRef={formRef}
        rowKey={rowKey}
        columns={finalColumns}
        request={isSimpleMode ? handleDataRequest : (request as any)}
        manualRequest={isSimpleMode ? false : manualRequest}
        search={finalFormFields.length > 0 ? buildSearchForm() : false}
        toolBarRender={buildToolBar as any}
        rowSelection={buildRowSelection()}
        pagination={
          isPagination
            ? {
                pageSize,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total: number) => `共 ${total} 条`,
              }
            : false
        }
        {...restProps}
      />
      {/* 批量操作工具栏 */}
      {enableRowSelection && selectedRows.length > 0 && (
        <FooterToolbar
          extra={
            <div>
              已选择 <a style={{ fontWeight: 600 }}>{selectedRows.length}</a> 项
            </div>
          }
        >
          {batchActions.map((action, index) => (
            <Button
              key={action.label || index}
              type={action.type}
              danger={action.danger}
              onClick={() => action.onClick?.(selectedRows)}
            >
              {action.icon}
              {action.label}
            </Button>
          ))}
        </FooterToolbar>
      )}
      {/* 详情抽屉 */}
      {detailDrawer?.enabled && (
        <Drawer
          width={detailDrawer.width || 600}
          open={showDetail}
          onClose={() => {
            setCurrentRow(undefined);
            setShowDetail(false);
          }}
          closable={false}
        >
          {currentRow && (
            <ProDescriptions<T>
              column={2}
              request={async () => {
                if (detailDrawer.getDetailData) {
                  const data = await Promise.resolve(
                    detailDrawer.getDetailData(currentRow),
                  );
                  return { data };
                }
                return { data: currentRow };
              }}
              columns={
                (detailDrawer.columns ||
                  columns) as ProDescriptionsItemProps<T>[]
              }
            />
          )}
        </Drawer>
      )}
    </PageContainer>
  );
}

export default CommonTable;
export type {
  CommonTableProps,
  FormFieldConfig,
  SimpleSearchItem,
  TableActionConfig,
} from './types';
